import { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, Pencil, Search, Pause, Play, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

function SortableTh({ label, sortKey, sort, onSort, align = "left" }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  const justify = align === "right" ? "justify-end" : "justify-start";
  const textAlign = align === "right" ? "text-right" : "text-left";
  return (
    <th className={`${textAlign} px-4 py-3`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`sort-${sortKey}`}
        aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        className={`inline-flex items-center gap-1 ${justify} font-mono uppercase tracking-widest text-[10px] hover:text-white transition-colors ${active ? "text-white" : "text-zinc-500"}`}
      >
        <span>{label}</span>
        <Icon className={`h-3 w-3 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}

function StatusBadge({ status, error, enabled, autoPausedAt }) {
  if (autoPausedAt) return <Badge className="rounded-sm bg-transparent border border-red-500/70 text-red-400 font-mono uppercase tracking-widest" data-testid="feed-status-badge" title="Auto-paused after too many consecutive failures">AUTO-PAUSED</Badge>;
  if (enabled === false) return <Badge className="rounded-sm bg-transparent border border-amber-500/60 text-amber-400 font-mono uppercase tracking-widest" data-testid="feed-status-badge">PAUSED</Badge>;
  if (status === "ok") return <Badge className="rounded-sm bg-transparent border border-emerald-500/60 text-emerald-400 font-mono uppercase tracking-widest" data-testid="feed-status-badge">OK</Badge>;
  if (status === "error") return <Badge className="rounded-sm bg-transparent border border-red-500/60 text-red-400 font-mono uppercase tracking-widest" data-testid="feed-status-badge" title={error}>ERR</Badge>;
  return <Badge className="rounded-sm bg-transparent border border-zinc-700 text-zinc-400 font-mono uppercase tracking-widest" data-testid="feed-status-badge">PENDING</Badge>;
}

function formatDelay(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60 * 10) / 10}h`;
  return `${Math.round(mins / 1440 * 10) / 10}d`;
}

function formatDuration(ms) {
  if (ms == null || ms === 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function relTime(iso) {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return "—"; }
}

const EMPTY = { name: "", url: "", interval_minutes: 15, enabled: true, user_agent: "", custom_headers: "", timeout_seconds: 15, category: "", stagger_slot_minute: null };

function FeedFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);

  // Reset form state when opening the dialog (edit vs add) or when switching feeds.
  // Without this, useState(initial) is only evaluated on first mount and subsequent
  // opens show either an empty form (bug reported) or the previous feed's data.
  useEffect(() => {
    if (open) {
      setForm(initial || EMPTY);
    }
  }, [open, initial]);

  // Load slot occupancy for the currently selected interval so we can show
  // "5/50" hints per slot in the dropdown.
  const interval = Number(form.interval_minutes) || 15;
  const { data: occupancy } = useQuery({
    queryKey: ["slot-occupancy", interval],
    queryFn: async () => (await api.get(`/feeds/slot-occupancy?interval_minutes=${interval}`)).data,
    enabled: open && interval >= 1 && interval <= 1440,
    staleTime: 15000,
  });

  const parseHeaders = () => {
    const t = (form.custom_headers || "").trim();
    if (!t) return {};
    try { return JSON.parse(t); } catch { return null; }
  };

  const submit = async (e) => {
    e.preventDefault();
    const headers = parseHeaders();
    if (headers === null) {
      toast.error("Custom headers must be valid JSON");
      return;
    }
    setSaving(true);
    try {
      const slotValRaw = form.stagger_slot_minute;
      const slotVal = slotValRaw === "" || slotValRaw === null || slotValRaw === undefined
        ? null
        : Number(slotValRaw);
      const payload = {
        name: form.name,
        url: form.url,
        interval_minutes: Number(form.interval_minutes) || 15,
        enabled: !!form.enabled,
        user_agent: form.user_agent || null,
        custom_headers: headers,
        timeout_seconds: Number(form.timeout_seconds) || 15,
        category: form.category || null,
        stagger_slot_minute: Number.isInteger(slotVal) ? slotVal : null,
      };
      if (initial?.id) {
        await api.patch(`/feeds/${initial.id}`, payload);
        toast.success("Feed updated");
      } else {
        await api.post("/feeds", payload);
        toast.success("Feed added");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0A0A0A] border-zinc-800 rounded-sm text-zinc-100" data-testid="feed-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono tracking-tight">{initial?.id ? "Edit Feed" : "Add Feed"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Name</Label>
            <Input data-testid="feed-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">RSS URL</Label>
            <Input data-testid="feed-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" placeholder="https://example.com/rss" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Interval (min)</Label>
              <Input data-testid="feed-interval-input" type="number" min="1" max="1440" value={form.interval_minutes} onChange={(e) => setForm({ ...form, interval_minutes: e.target.value })} className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Timeout (s)</Label>
              <Input type="number" min="3" max="60" value={form.timeout_seconds} onChange={(e) => setForm({ ...form, timeout_seconds: e.target.value })} className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">User-Agent (optional)</Label>
            <Input value={form.user_agent} onChange={(e) => setForm({ ...form, user_agent: e.target.value })} className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" placeholder="Mozilla/5.0 …" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Custom Headers (JSON, optional)</Label>
            <Textarea rows={3} value={form.custom_headers} onChange={(e) => setForm({ ...form, custom_headers: e.target.value })} className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono text-xs" placeholder='{ "Referer": "https://example.com" }' />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Minute slot
              <span className="ml-2 text-zinc-600 normal-case tracking-normal">(when in the {interval}-min window this feed fires)</span>
            </Label>
            <select
              data-testid="feed-slot-select"
              value={form.stagger_slot_minute === null || form.stagger_slot_minute === undefined ? "" : String(form.stagger_slot_minute)}
              onChange={(e) => setForm({ ...form, stagger_slot_minute: e.target.value === "" ? null : Number(e.target.value) })}
              className="mt-1 w-full bg-transparent border border-zinc-800 rounded-sm font-mono text-sm px-2 py-2 text-zinc-200"
            >
              <option value="">Auto (server picks the least-full slot)</option>
              {occupancy?.slots?.map((s) => {
                const isCurrent = initial?.stagger_slot_minute === s.slot;
                // Feeds count in this slot excluding this one when editing
                const others = isCurrent ? Math.max(0, s.count - 1) : s.count;
                const full = others >= (occupancy.soft_cap ?? 50);
                return (
                  <option key={s.slot} value={String(s.slot)}>
                    :{String(s.slot).padStart(2, "0")}   ·   {others}/{occupancy.soft_cap ?? 50}{full ? "  · FULL" : ""}
                  </option>
                );
              })}
            </select>
            {occupancy && form.stagger_slot_minute != null && (() => {
              const slot = occupancy.slots.find((s) => s.slot === Number(form.stagger_slot_minute));
              if (!slot) return null;
              const isCurrent = initial?.stagger_slot_minute === slot.slot;
              const others = isCurrent ? Math.max(0, slot.count - 1) : slot.count;
              const cap = occupancy.soft_cap ?? 50;
              return (
                <div className={`mt-1 text-[10px] font-mono ${others >= cap ? "text-red-400" : others >= cap * 0.8 ? "text-amber-400" : "text-zinc-500"}`} data-testid="feed-slot-hint">
                  {others >= cap
                    ? `Slot :${String(slot.slot).padStart(2,"0")} is at capacity (${others}/${cap}). Pick another to spread load.`
                    : `Slot :${String(slot.slot).padStart(2,"0")} has ${others} other feed${others === 1 ? "" : "s"} (cap ${cap}).`}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} id="enabled-switch" />
            <Label htmlFor="enabled-switch" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Enabled</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm border-zinc-800 bg-transparent" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="feed-save-btn" type="submit" disabled={saving} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono">
              {saving ? "SAVING…" : "SAVE"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Feeds() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const { data: feeds = [], isLoading } = useQuery({
    queryKey: ["feeds", q, statusFilter],
    queryFn: async () => (await api.get("/feeds", { params: { q: q || undefined, status: statusFilter || undefined } })).data,
    refetchInterval: 20000,
  });

  // Rank feeds by a normalized status so sorting groups by health.
  const statusRank = (f) => {
    if (f.auto_paused_at) return 4;          // auto-paused (worst)
    if (f.enabled === false) return 3;        // paused
    if (f.last_status === "error") return 2;  // erroring
    if (f.last_status === "ok") return 0;     // healthy (best → top on asc)
    return 1;                                 // pending
  };

  const sortValue = (f, key) => {
    switch (key) {
      case "name": return (f.name || "").toLowerCase();
      case "url": return (f.url || "").toLowerCase();
      case "status": return statusRank(f);
      case "interval": return f.interval_minutes ?? 0;
      case "slot": return f.stagger_slot_minute ?? Number.POSITIVE_INFINITY;
      case "items": return f.last_items_count ?? 0;
      case "speed": return f.avg_fetch_duration_ms ?? Number.POSITIVE_INFINITY; // unknown → last
      case "last_scrape": return f.last_fetch_at ? new Date(f.last_fetch_at).getTime() : 0;
      case "last_article": return f.last_published_at ? new Date(f.last_published_at).getTime() : 0;
      case "next": return f.next_fetch_at ? new Date(f.next_fetch_at).getTime() : Number.POSITIVE_INFINITY;
      default: return 0;
    }
  };

  const sortedFeeds = useMemo(() => {
    if (!sort.key) return feeds;
    const arr = [...feeds];
    const mult = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeds, sort]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" }; // third click clears sort
    });
  };

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/feeds/${id}`),
    onSuccess: () => { toast.success("Feed deleted"); qc.invalidateQueries({ queryKey: ["feeds"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const fetchNow = useMutation({
    mutationFn: async (id) => api.post(`/feeds/${id}/fetch-now`),
    onSuccess: () => { toast.success("Fetch triggered"); qc.invalidateQueries({ queryKey: ["feeds"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const togglePause = useMutation({
    mutationFn: async ({ id, enabled }) => api.patch(`/feeds/${id}`, { enabled }),
    onSuccess: (_r, vars) => { toast.success(vars.enabled ? "Feed resumed" : "Feed paused"); qc.invalidateQueries({ queryKey: ["feeds"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const startEdit = (f) => {
    setEditing({
      ...f,
      custom_headers: f.custom_headers && Object.keys(f.custom_headers).length ? JSON.stringify(f.custom_headers, null, 2) : "",
      user_agent: f.user_agent || "",
      category: f.category || "",
      stagger_slot_minute: f.stagger_slot_minute ?? null,
    });
    setOpen(true);
  };

  const startAdd = () => { setEditing(null); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="feeds-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Sources</div>
          <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">Feed Manager</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              data-testid="feed-search-input"
              placeholder="Search name or URL"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-64 bg-transparent border-zinc-800 rounded-sm font-mono text-sm"
            />
          </div>
          <select
            data-testid="feed-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-transparent border border-zinc-800 rounded-sm font-mono text-sm px-2 py-2 text-zinc-200"
          >
            <option value="">All statuses</option>
            <option value="ok">OK</option>
            <option value="error">Error</option>
            <option value="pending">Pending</option>
            <option value="paused">Paused</option>
            <option value="auto-paused">Auto-paused</option>
          </select>
          <Button data-testid="add-feed-btn" onClick={startAdd} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono gap-2">
            <Plus className="h-4 w-4" /> Add Feed
          </Button>
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
              <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} align="left" />
              <SortableTh label="URL" sortKey="url" sort={sort} onSort={toggleSort} align="left" />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} align="left" />
              <SortableTh label="Interval" sortKey="interval" sort={sort} onSort={toggleSort} align="right" />
              <SortableTh label="Slot" sortKey="slot" sort={sort} onSort={toggleSort} align="right" />
              <SortableTh label="Items" sortKey="items" sort={sort} onSort={toggleSort} align="right" />
              <SortableTh label="Speed" sortKey="speed" sort={sort} onSort={toggleSort} align="right" />
              <SortableTh label="Last scrape" sortKey="last_scrape" sort={sort} onSort={toggleSort} align="left" />
              <SortableTh label="Last article" sortKey="last_article" sort={sort} onSort={toggleSort} align="left" />
              <SortableTh label="Next" sortKey="next" sort={sort} onSort={toggleSort} align="left" />
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="feeds-tbody">
            {isLoading && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-zinc-500 font-mono text-xs">LOADING…</td></tr>
            )}
            {!isLoading && sortedFeeds.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-zinc-500 font-mono text-xs">— no feeds yet, add one to start —</td></tr>
            )}
            {sortedFeeds.map((f) => (
              <tr key={f.id} data-testid={`feed-row-${f.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/60">
                <td className="px-4 py-3 font-mono">
                  <div className="text-white truncate max-w-[200px]">{f.name}</div>
                  {f.category && <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{f.category}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400 truncate max-w-[260px]" title={f.url}>{f.url}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={f.last_status} error={f.last_error} enabled={f.enabled} autoPausedAt={f.auto_paused_at} />
                  {f.consecutive_failures > 0 && !f.auto_paused_at && (
                    <div
                      data-testid={`backoff-indicator-${f.id}`}
                      className="mt-1 text-[10px] font-mono text-amber-400"
                      title={`Backoff × ${f.consecutive_failures} — next retry in ${formatDelay(f.next_delay_minutes)}`}
                    >
                      backoff ×{f.consecutive_failures} · {formatDelay(f.next_delay_minutes)}
                    </div>
                  )}
                  {f.last_retries_used > 0 && f.last_status === "ok" && (
                    <div className="mt-1 text-[10px] font-mono text-zinc-500" title="Inline retries used on last fetch">
                      recovered ({f.last_retries_used} retries)
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-right">{f.interval_minutes}m</td>
                <td className="px-4 py-3 font-mono text-right text-zinc-400" data-testid={`feed-slot-${f.id}`}>
                  {f.stagger_slot_minute != null ? `:${String(f.stagger_slot_minute).padStart(2, "0")}` : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 font-mono text-right">{f.last_items_count ?? 0}</td>
                <td className="px-4 py-3 font-mono text-right text-zinc-300" title={f.last_fetch_duration_ms != null ? `Last: ${formatDuration(f.last_fetch_duration_ms)} · Avg: ${formatDuration(f.avg_fetch_duration_ms)}` : ""}>
                  {formatDuration(f.avg_fetch_duration_ms)}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{relTime(f.last_fetch_at)}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {f.last_published_at ? (
                    <span className={(() => {
                      try {
                        const days = (Date.now() - new Date(f.last_published_at).getTime()) / 86400000;
                        if (days > 30) return "text-red-400";
                        if (days > 7) return "text-amber-400";
                        return "text-zinc-300";
                      } catch { return "text-zinc-400"; }
                    })()} title={f.last_published_at}>
                      {relTime(f.last_published_at)}
                    </span>
                  ) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{relTime(f.next_fetch_at)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button data-testid={`fetch-now-btn-${f.id}`} size="sm" variant="outline" className="h-8 rounded-sm border-zinc-800 bg-transparent" onClick={() => fetchNow.mutate(f.id)} title="Fetch now">
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      data-testid={`pause-feed-btn-${f.id}`}
                      size="sm"
                      variant="outline"
                      className={cn("h-8 rounded-sm bg-transparent", f.enabled === false ? "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" : "border-zinc-800")}
                      onClick={() => togglePause.mutate({ id: f.id, enabled: !(f.enabled !== false) })}
                      title={f.enabled === false ? "Resume" : "Pause"}
                    >
                      {f.enabled === false ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                    </Button>
                    <Button data-testid={`edit-feed-btn-${f.id}`} size="sm" variant="outline" className="h-8 rounded-sm border-zinc-800 bg-transparent" onClick={() => startEdit(f)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button data-testid={`delete-feed-btn-${f.id}`} size="sm" variant="outline" className="h-8 rounded-sm border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10" onClick={() => { if (confirm(`Delete "${f.name}"?`)) del.mutate(f.id); }} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <FeedFormDialog
        open={open}
        onOpenChange={setOpen}
        initial={editing}
        onSaved={() => qc.invalidateQueries({ queryKey: ["feeds"] })}
      />
    </div>
  );
}
